import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';
const STORAGE_KEY = 'sw-theme';

interface ThemeContextValue {
  theme: ThemePreference;
  resolved: Resolved;
  setTheme: (t: ThemePreference) => void;
  isTelegram: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** True only when the page was opened as a real Mini App (initData present). */
export function detectTelegramMiniApp(): boolean {
  const tg = (window as any).Telegram?.WebApp;
  return Boolean(tg && typeof tg.initData === 'string' && tg.initData.length > 0) ||
    window.location.pathname.startsWith('/tg');
}

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isTelegram = useMemo(detectTelegramMiniApp, []);
  const [theme, setThemeState] = useState<ThemePreference>(readStored);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);
  const [tgScheme, setTgScheme] = useState<Resolved | null>(() =>
    isTelegram ? ((window as any).Telegram?.WebApp?.colorScheme === 'dark' ? 'dark' : 'light') : null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (!isTelegram || !tg?.onEvent) return;
    const onTheme = () => setTgScheme(tg.colorScheme === 'dark' ? 'dark' : 'light');
    tg.onEvent('themeChanged', onTheme);
    return () => tg.offEvent?.('themeChanged', onTheme);
  }, [isTelegram]);

  const resolved: Resolved = tgScheme ?? (theme === 'system' ? (systemDark ? 'dark' : 'light') : theme);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0D0F1E' : '#F5F6FB');
  }, [resolved]);

  const setTheme = useCallback((t: ThemePreference) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* storage blocked */ }
  }, []);

  const value = useMemo(() => ({ theme, resolved, setTheme, isTelegram }), [theme, resolved, setTheme, isTelegram]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
